import { useEffect, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import FormInput from "../../components/CustomComponents/Input/formInput";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";

const emptyForm = {
  serviceName: "",
  serviceDescription: "",
  price: "",
};

function CateringServiceModal({
  isModalOpen,
  setIsModalOpen,
  onServiceSaved,
  compID,
  serviceToEdit = null,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const currencySymbol = currencySymbols[compCurrency] || compCurrency || "£";

  useEffect(() => {
    if (serviceToEdit) {
      setForm({
        serviceName: serviceToEdit.serviceName ?? serviceToEdit.ServiceName ?? "",
        serviceDescription:
          serviceToEdit.serviceDescription ?? serviceToEdit.ServiceDescription ?? "",
        price: String(serviceToEdit.price ?? serviceToEdit.Price ?? 0),
      });
    } else {
      setForm(emptyForm);
    }
  }, [serviceToEdit, isModalOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    const name = form.serviceName?.trim();
    if (!name) {
      showErrorToast("Please provide a valid service name.");
      return;
    }

    if (!compID || Number(compID) <= 0) {
      showErrorToast("Company context is missing. Please sign in again.");
      return;
    }

    const priceNum = parseFloat(form.price);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      showErrorToast("Please provide a valid price.");
      return;
    }

    const isUpdate = Boolean(
      serviceToEdit?.cateringService_ID ?? serviceToEdit?.CateringService_ID
    );

    const payload = {
      cateringService_ID: isUpdate
        ? Number(serviceToEdit.cateringService_ID ?? serviceToEdit.CateringService_ID)
        : 0,
      cateringService_Guid: isUpdate
        ? serviceToEdit?.cateringService_Guid ?? serviceToEdit?.CateringService_Guid ?? null
        : null,
      compID: Number(compID),
      serviceName: name,
      serviceDescription: form.serviceDescription?.trim() || null,
      price: priceNum,
      isActive: serviceToEdit?.isActive ?? serviceToEdit?.IsActive ?? true,
    };

    try {
      setSaving(true);
      const response = await axiosInstance.post(APIs.POST_CATERING_SERVICE, payload);

      if (response.status !== 200 || !response.data?.status) {
        showErrorToast(response.data?.message || "Operation failed.");
        return;
      }

      const saved =
        response.data?.data ??
        response.data?.Data ??
        null;
      if (!saved || !(saved.cateringService_ID ?? saved.CateringService_ID)) {
        showErrorToast(
          "Save did not return a service record. Ensure tbl_CateringServices and SP_InsertCateringService exist on the API database."
        );
        return;
      }

      showSuccessToast(
        isUpdate ? "Service updated successfully." : "Service created successfully."
      );
      onServiceSaved?.(saved, isUpdate);
      handleCloseModal();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Something went wrong while saving the service."
      );
    } finally {
      setSaving(false);
    }
  };

  const textareaBorder = isDarkMode ? "#6b7280" : "#d1d5db";
  const textareaFocusBorder = themeColor;

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      onSubmit={handleSubmit}
      title={serviceToEdit ? "Update service" : "Create new service"}
      showButton={true}
      isCentered={true}
      dialogMaxWidth="640px"
      submitButtonText={serviceToEdit ? "Update Service" : "Save New Service"}
      submitButtonDisabled={saving}
      submitButtonStyle={{
        width: "auto",
        minWidth: "160px",
        whiteSpace: "nowrap",
        paddingLeft: "20px",
        paddingRight: "20px",
      }}
      themeColor={themeColor}
      minHeight="auto"
      maxHeight="90vh"
    >
      <div className="row g-3 mb-2">
        <div className="col-md-8">
          <FormInput
            label="Service Name"
            placeHolder="Silver Package"
            type="text"
            value={form.serviceName}
            onchange={(e) =>
              setForm((prev) => ({ ...prev, serviceName: e.target.value }))
            }
            required
          />
        </div>
        <div className="col-md-4">
          <FormInput
            label="Price"
            placeHolder="0.00"
            type="number"
            step="0.01"
            min="0"
            prepend={currencySymbol}
            value={form.price}
            onchange={(e) =>
              setForm((prev) => ({ ...prev, price: e.target.value }))
            }
            required
          />
        </div>
        <div className="col-12">
          <label
            className="form-label"
            style={{ color: isDarkMode ? "#ffffff" : "#6c757d" }}
          >
            Service Description
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Describe your service briefly."
            value={form.serviceDescription}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                serviceDescription: e.target.value,
              }))
            }
            style={{
              borderRadius: "10px",
              border: `1px solid ${textareaBorder}`,
              minHeight: "120px",
              resize: "vertical",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = textareaFocusBorder;
              e.target.style.boxShadow = `0 0 0 2px ${themeColor}33`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = textareaBorder;
              e.target.style.boxShadow = "none";
            }}
          />
        </div>
      </div>
    </CustomModal>
  );
}

export default CateringServiceModal;
